import React from "react";
import { useRental } from "../../../contexts/api";
import type {
  Rental,
  RentalAddItemRequest,
  RentalCreateRequest,
  RentalListFilters,
  RentalUpdateRequest,
} from "../../../api/rental/Rental.typed";

// API may return `items: null` (or undefined) when the basket is empty; UI assumes an array.
function normalizeRental(r: Rental): Rental {
  return { ...r, items: r.items ?? [] };
}

export default function useRentals() {
  const {
    listRentals,
    getRental,
    createRental,
    updateRental,
    deleteRental,
    addRentalItem,
    returnRentalItem,
    removeRentalItem,
  } = useRental();

  const [rentals, setRentals] = React.useState<Rental[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [filters, setFilters] = React.useState<RentalListFilters | undefined>(undefined);

  const refresh = React.useCallback(
    async (next?: RentalListFilters) => {
      setLoading(true);
      const res = await listRentals(next ?? filters);
      setRentals((res ?? []).map(normalizeRental));
      setLoading(false);
    },
    [listRentals, filters],
  );

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const create = React.useCallback(
    async (req: RentalCreateRequest) => {
      const created = await createRental(req);
      if (created) await refresh();
      return created ? normalizeRental(created) : created;
    },
    [createRental, refresh],
  );

  const update = React.useCallback(
    async (id: number, req: RentalUpdateRequest) => {
      const updated = await updateRental(id, req);
      if (updated) await refresh();
      return updated;
    },
    [updateRental, refresh],
  );

  const remove = React.useCallback(
    async (id: number) => {
      const ok = await deleteRental(id);
      if (ok) await refresh();
      return ok;
    },
    [deleteRental, refresh],
  );

  const addItem = React.useCallback(
    async (rentalID: number, req: RentalAddItemRequest) => {
      const created = await addRentalItem(rentalID, req);
      return created;
    },
    [addRentalItem],
  );

  const returnItem = React.useCallback(
    async (rentalID: number, itemID: number, state: "returned" | "not_returned" = "returned") => {
      return returnRentalItem(rentalID, itemID, state);
    },
    [returnRentalItem],
  );

  const removeItem = React.useCallback(
    async (rentalID: number, itemID: number) => {
      return removeRentalItem(rentalID, itemID);
    },
    [removeRentalItem],
  );

  const reload = React.useCallback(
    async (id: number) => {
      const r = await getRental(id);
      return r ? normalizeRental(r) : r;
    },
    [getRental],
  );

  return {
    rentals,
    loading,
    filters,
    setFilters,
    refresh,
    create,
    update,
    remove,
    addItem,
    returnItem,
    removeItem,
    reload,
  };
}
